import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { request } from "../../../utils/request";

// Fetch disclaimer - first check user's disclaimers, then fall back to templates
export const fetchDisclaimer = createAsyncThunk(
  "disclaimer/fetchDisclaimer",
  async (_, { rejectWithValue }) => {
    try {
      // First, check if user has any disclaimers
      const userDisclaimers = await request(
        `${process.env.REACT_APP_API_URL}/disclaimers/`,
        { method: "GET" }
      );

      const disclaimerList = userDisclaimers?.results || userDisclaimers || [];

      if (disclaimerList.length > 0) {
        // User has disclaimers - use the one with is_default or first one
        const disclaimer =
          disclaimerList.find((d) => d.is_default) || disclaimerList[0];
        return {
          name: disclaimer?.name || "",
          content: disclaimer?.content || "",
          isHiddenFromReports: !disclaimer?.is_default,
          disclaimerId: disclaimer?.id || null,
          isFromTemplate: false,
        };
      }

      // No user disclaimers - fetch templates as placeholder
      const templates = await request(
        `${process.env.REACT_APP_API_URL}/disclaimers/templates/`,
        { method: "GET" }
      );

      const templateList = templates?.results || templates || [];
      const template = templateList[0] || null;

      return {
        name: template?.name || "",
        content: template?.content || "",
        isHiddenFromReports: true, // Default to inactive for templates
        disclaimerId: template?.id || null,
        isFromTemplate: true,
      };
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch disclaimer");
    }
  }
);

// Save disclaimer content
export const saveDisclaimer = createAsyncThunk(
  "disclaimer/saveDisclaimer",
  async (content, { rejectWithValue, getState }) => {
    try {
      const { disclaimer } = getState();
      const { disclaimerId, isFromTemplate, name, isHiddenFromReports } =
        disclaimer;

      // API uses is_default: true = show, false = hide
      const isDefault = !isHiddenFromReports;

      if (isFromTemplate) {
        // User is saving a template for the first time - POST to create
        const response = await request(
          `${process.env.REACT_APP_API_URL}/disclaimers/`,
          {
            method: "POST",
            body: JSON.stringify({
              name: name,
              content: content,
              is_default: isDefault,
            }),
          }
        );

        return {
          content: response.content,
          name: response.name,
          disclaimerId: response.id,
          isFromTemplate: false,
        };
      } else {
        // User already has a disclaimer - PUT to update
        const response = await request(
          `${process.env.REACT_APP_API_URL}/disclaimers/${disclaimerId}/`,
          {
            method: "PUT",
            body: JSON.stringify({
              name: name,
              content: content,
              is_default: isDefault,
            }),
          }
        );

        return {
          content: response.content,
          name: response.name,
        };
      }
    } catch (error) {
      return rejectWithValue(error.message || "Failed to save disclaimer");
    }
  }
);

// Toggle disclaimer visibility in reports
// isHiddenFromReports: true = hide, false = show
// API uses is_default: true = show, false = hide (inverted)
export const toggleDisclaimerVisibility = createAsyncThunk(
  "disclaimer/toggleVisibility",
  async (isHiddenFromReports, { rejectWithValue, getState }) => {
    try {
      const { disclaimer } = getState();
      const { disclaimerId, isFromTemplate } = disclaimer;

      if (!disclaimerId) {
        return rejectWithValue("No disclaimer found");
      }

      // API uses is_default: true = show, false = hide
      const isDefault = !isHiddenFromReports;

      if (isFromTemplate && !isHiddenFromReports) {
        // User is showing a template for the first time - POST to create
        const { name, content } = disclaimer;

        const response = await request(
          `${process.env.REACT_APP_API_URL}/disclaimers/`,
          {
            method: "POST",
            body: JSON.stringify({
              name: name,
              content: content,
              is_default: isDefault,
            }),
          }
        );

        return {
          isHiddenFromReports,
          disclaimerId: response?.id || disclaimerId,
          isFromTemplate: false,
        };
      } else {
        // User already has a disclaimer - PATCH to update
        await request(
          `${process.env.REACT_APP_API_URL}/disclaimers/${disclaimerId}/`,
          {
            method: "PATCH",
            body: JSON.stringify({ is_default: isDefault }),
          }
        );

        return { isHiddenFromReports };
      }
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to update disclaimer visibility"
      );
    }
  }
);

const initialState = {
  name: "",
  content: "",
  disclaimerId: null,
  isFromTemplate: true, // Whether the content is from a template (not yet saved)
  isHiddenFromReports: true, // Default to hidden (inactive)
  fetchStatus: "idle", // idle | loading | success | failed
  fetchError: null,
  saveStatus: "idle", // idle | loading | success | failed
  saveError: null,
  visibilityStatus: "idle", // idle | loading | success | failed
  visibilityError: null,
};

const disclaimerSlice = createSlice({
  name: "disclaimer",
  initialState,
  reducers: {
    clearSaveStatus: (state) => {
      state.saveStatus = "idle";
      state.saveError = null;
    },
    clearVisibilityStatus: (state) => {
      state.visibilityStatus = "idle";
      state.visibilityError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch disclaimer
      .addCase(fetchDisclaimer.pending, (state) => {
        state.fetchStatus = "loading";
        state.fetchError = null;
      })
      .addCase(fetchDisclaimer.fulfilled, (state, action) => {
        state.fetchStatus = "success";
        state.name = action.payload.name;
        state.content = action.payload.content;
        state.disclaimerId = action.payload.disclaimerId;
        state.isFromTemplate = action.payload.isFromTemplate;
        state.isHiddenFromReports = action.payload.isHiddenFromReports ?? true;
        state.fetchError = null;
      })
      .addCase(fetchDisclaimer.rejected, (state, action) => {
        state.fetchStatus = "failed";
        state.fetchError = action.payload;
      })
      // Save disclaimer
      .addCase(saveDisclaimer.pending, (state) => {
        state.saveStatus = "loading";
        state.saveError = null;
      })
      .addCase(saveDisclaimer.fulfilled, (state, action) => {
        state.saveStatus = "success";
        state.content = action.payload.content;
        if (action.payload.name !== undefined) {
          state.name = action.payload.name;
        }
        // Update disclaimerId and isFromTemplate if provided (after POST from template)
        if (action.payload.disclaimerId !== undefined) {
          state.disclaimerId = action.payload.disclaimerId;
        }
        if (action.payload.isFromTemplate !== undefined) {
          state.isFromTemplate = action.payload.isFromTemplate;
        }
        state.saveError = null;
      })
      .addCase(saveDisclaimer.rejected, (state, action) => {
        state.saveStatus = "failed";
        state.saveError = action.payload;
      })
      // Toggle visibility
      .addCase(toggleDisclaimerVisibility.pending, (state) => {
        state.visibilityStatus = "loading";
        state.visibilityError = null;
      })
      .addCase(toggleDisclaimerVisibility.fulfilled, (state, action) => {
        state.visibilityStatus = "success";
        state.isHiddenFromReports = action.payload.isHiddenFromReports;
        // Update disclaimerId and isFromTemplate if provided (after POST from template)
        if (action.payload.disclaimerId !== undefined) {
          state.disclaimerId = action.payload.disclaimerId;
        }
        if (action.payload.isFromTemplate !== undefined) {
          state.isFromTemplate = action.payload.isFromTemplate;
        }
        state.visibilityError = null;
      })
      .addCase(toggleDisclaimerVisibility.rejected, (state, action) => {
        state.visibilityStatus = "failed";
        state.visibilityError = action.payload;
      });
  },
});

export const { clearSaveStatus, clearVisibilityStatus } =
  disclaimerSlice.actions;
export default disclaimerSlice.reducer;
